'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'

export interface TooltipData {
  type: 'hardware' | 'software'
  genericName: string
  genericDescription: string
  proprietaryName: string | null
  proprietaryDescription: string | null
  disclaimer: boolean
  brandName: string
  descriptionViaText: string
  /** Multi-generic product groups label each child by its canonical mapping. */
  genericFirst?: boolean
}

interface OpenTooltip {
  data: TooltipData
  anchorTop: number
  anchorBottom: number
  anchorCenter: number
}

interface TooltipPosition {
  top: number
  left: number
}

const VIEWPORT_GAP = 16
const ANCHOR_GAP = 8

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum))

/**
 * Attaches click-to-show tooltips to any element with [data-spec-tooltip] inside the container.
 * The tooltip data is stored as JSON in the data attribute.
 */
export const FeatureTooltipProvider = ({
  children,
  tooltips = {},
}: {
  children: React.ReactNode
  tooltips?: Record<string, TooltipData>
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  // Read tooltips through a ref so the document handler always sees the latest
  // merged map (on-demand panels add tooltips after mount) without re-subscribing.
  const tooltipsRef = useRef(tooltips)
  tooltipsRef.current = tooltips
  const [tooltip, setTooltip] = useState<OpenTooltip | null>(null)
  const [position, setPosition] = useState<TooltipPosition | null>(null)

  useLayoutEffect(() => {
    const popover = popoverRef.current
    if (!tooltip || !popover) {
      return
    }

    const rect = popover.getBoundingClientRect()
    const viewportLeft = window.scrollX + VIEWPORT_GAP
    const viewportRight = window.scrollX + window.innerWidth - VIEWPORT_GAP
    const viewportTop = window.scrollY + VIEWPORT_GAP
    const viewportBottom = window.scrollY + window.innerHeight - VIEWPORT_GAP
    const spaceAbove = tooltip.anchorTop - ANCHOR_GAP - viewportTop
    const spaceBelow = viewportBottom - tooltip.anchorBottom - ANCHOR_GAP
    const placeAbove = spaceAbove >= rect.height || spaceAbove >= spaceBelow
    const preferredTop = placeAbove ? tooltip.anchorTop - ANCHOR_GAP - rect.height : tooltip.anchorBottom + ANCHOR_GAP

    setPosition({
      top: clamp(preferredTop, viewportTop, viewportBottom - rect.height),
      left: clamp(tooltip.anchorCenter - rect.width / 2, viewportLeft, viewportRight - rect.width),
    })
  }, [tooltip])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const handleClick = (e: MouseEvent) => {
      if (!(e.target instanceof Element)) {
        return
      }
      const target = e.target.closest('[data-spec-tooltip],[data-spec-tooltip-id]') as HTMLElement | null
      // Scope to our own container: a page can mount several providers (the prices
      // section and the comparison panels), and tooltip ids are content-hashed, so
      // the same feature resolves in BOTH maps. Without this, one click on a shared
      // feature would open a popover from every provider, stacked on top of another.
      if (target && container.contains(target)) {
        e.preventDefault()
        try {
          const id = target.getAttribute('data-spec-tooltip-id')
          const data = id
            ? tooltipsRef.current[id]
            : (JSON.parse(target.getAttribute('data-spec-tooltip')!) as TooltipData)
          if (!data) {
            return
          }
          const rect = target.getBoundingClientRect()
          setPosition(null)
          setTooltip({
            data,
            anchorTop: rect.top + window.scrollY,
            anchorBottom: rect.bottom + window.scrollY,
            anchorCenter: rect.left + window.scrollX + rect.width / 2,
          })
        } catch {
          // ignore parse errors
        }
        return
      }

      // Click outside — close
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setTooltip(null)
        setPosition(null)
      }
    }

    const closeTooltip = () => {
      setTooltip(null)
      setPosition(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTooltip()
      }
    }

    document.addEventListener('click', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('scroll', closeTooltip, true)
    window.addEventListener('resize', closeTooltip)
    return () => {
      document.removeEventListener('click', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('scroll', closeTooltip, true)
      window.removeEventListener('resize', closeTooltip)
    }
    // Bind once; the latest tooltips map is read via tooltipsRef inside the handler.
  }, [])

  return (
    <div ref={containerRef}>
      {children}
      {tooltip &&
        createPortal(
          <div
            ref={popoverRef}
            role="tooltip"
            aria-live="polite"
            className="pointer-events-auto absolute z-[9999] w-max min-w-[min(200px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] sm:max-w-[500px]"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            <div className="max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg bg-white px-4 py-3 shadow-lg ring-1 ring-slate-200">
              {tooltip.data.type === 'software' && tooltip.data.genericFirst && (
                <>
                  <p className="text-navy text-sm font-bold">{tooltip.data.genericName}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">{tooltip.data.genericDescription}</p>
                  {tooltip.data.proprietaryName && tooltip.data.proprietaryDescription && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <p className="text-navy text-xs font-bold">{tooltip.data.proprietaryName}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-500">
                        {tooltip.data.proprietaryDescription}
                      </p>
                      {tooltip.data.disclaimer && (
                        <p className="mt-2 text-xs text-slate-400 italic">{tooltip.data.descriptionViaText}</p>
                      )}
                    </div>
                  )}
                </>
              )}
              {tooltip.data.type === 'software' && !tooltip.data.genericFirst && (
                <>
                  <p className="text-navy text-sm font-bold">
                    {tooltip.data.proprietaryName || tooltip.data.genericName}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">
                    {tooltip.data.proprietaryDescription || tooltip.data.genericDescription}
                  </p>
                  {tooltip.data.disclaimer && tooltip.data.proprietaryDescription && (
                    <p className="mt-2 text-xs text-slate-400 italic">{tooltip.data.descriptionViaText}</p>
                  )}
                </>
              )}
              {tooltip.data.type === 'hardware' && (
                <>
                  <p className="text-navy text-sm font-bold">{tooltip.data.genericName}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">{tooltip.data.genericDescription}</p>
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
